import React, { Suspense, useState, useEffect } from 'react'
import { LinearProgress } from '@mui/material'
import { MenuItem } from '../types/menuItem.types'
import { Route } from '../types/route.types'
import {
  AddCardIcon,
  AnalyticsIcon,
  BookingIcon,
  CustomersIcon,
  EditCalendarIcon,
  EmailIcon,
  PricingIcon,
  SettingsIcon,
  UmbrellaIcon,
} from '../components/styles/Icons.styled'
import { useTranslation } from 'react-i18next'
import { PlanLevel } from '../features/Configuration/GeneralConfiguration/types/types'
import { StaticPages } from '../pages/StaticPage/types'

/*-------------------------------------------------------------------------------
*                          LAZY-LOADED PAGES
*  Each page is loaded on demand via React.lazy() to reduce the initial bundle.
*  Public, Customer, Manager, Owner, and Admin pages are split into separate chunks.
*  Each page is wrapped in its own Suspense boundary so the layout (header, menus)
*  stays mounted while only the page content shows a loading bar.
-------------------------------------------------------------------------------*/

const LOADER_DELAY_MS = 300

const PageLoadingFallback = () => {
  const [showLoader, setShowLoader] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowLoader(true), LOADER_DELAY_MS)
    return () => clearTimeout(timer)
  }, [])

  if (!showLoader) return null

  return <LinearProgress sx={{ height: '5px' }} />
}

const lazyPage = (factory: () => Promise<{ default: React.ComponentType }>) => {
  const LazyComponent = React.lazy(factory)
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <LazyComponent />
    </Suspense>
  )
}

// Public pages
const LoginPage = lazyPage(() => import('../pages/LoginPage/LoginPage'))

// Customer pages
const HomePage = lazyPage(() => import('../pages/HomePage/HomePage'))
const CheckoutPage = lazyPage(() => import('../pages/CheckoutPage/CheckoutPage'))
const OutcomePage = lazyPage(() => import('../pages/OutcomePage/OutcomePage'))
const StaticPageLazy = React.lazy(() => import('../pages/StaticPage/StaticPage'))

const staticPageElement = (page: StaticPages) => (
  <Suspense fallback={<PageLoadingFallback />}>
    <StaticPageLazy page={page} />
  </Suspense>
)

// Manager / Owner / Admin pages
const BookingsPage = lazyPage(() => import('../pages/BookingsPage/BookingsPage'))
const BeachPlanPage = lazyPage(() => import('../pages/BeachPlanPage/BeachPlanPage'))
const AnalyticsPage = lazyPage(() => import('../pages/AnalyticsPage/AnalyticsPage'))
const CustomersPage = lazyPage(() => import('../pages/CustomersPage/CustomersPage'))

// Admin configuration pages
const SettingsBeachPlanPage = lazyPage(() => import('../pages/BeachPlanConfigurationPage/BeachPlanConfigurationPage'))
const SettingsPaymentPage = lazyPage(() => import('../pages/PaymentConfigurationPage/PaymentConfigurationPage'))
const SettingsAvailabilityPage = lazyPage(() => import('../pages/AvailabilityConfigurationPage/AvailabilityConfigurationPage'))
const SettingsPricingPage = lazyPage(() => import('../pages/PricingConfigurationPage/PricingConfigurationPage'))
const SettingsGeneralPage = lazyPage(() => import('../pages/GeneralConfigurationPage/GeneralConfigurationPage'))
const EmailConfigurationPage = lazyPage(() => import('../pages/EmailConfigurationPage/EmailConfigurationPage'))

/*-------------------------------------------------------------------------------
*                               ROUTES
-------------------------------------------------------------------------------*/

//---------------------------- Customer Routes ----------------------------
export enum CustomerRoutes {
  HOME = '/booking',
  CHECKOUT = '/checkout',
  BOOKINGS = '/bookings',
  OUTCOME = '/outcome/:outcome/:reservationId?',
  CONTACT = '/contact',
  SERVICES = '/services',
  PRICELIST = '/pricelist',
  RESERVATION_POLICY = '/reservation-policy',
  PRIVACY_POLICY = '/privacy-policy',
}

//---------------------------- MANAGER Routes ----------------------------
export const ManagerRoutes = {
  BOOKINGS: '/manager/bookings',
  BEACH_PLAN: '/manager/beach-plan',
} as const

//---------------------------- WAITER Routes ----------------------------
export const WaiterRoutes = {
  BOOKINGS: '/waiter/bookings',
  BEACH_PLAN: '/waiter/beach-plan',
} as const

//---------------------------- OWNER Routes ----------------------------
export const OwnerRoutes = {
  BOOKINGS: '/owner/bookings',
  BEACH_PLAN: '/owner/beach-plan',
  ANALYTICS: '/owner/analytics',
  CUSTOMERS: '/owner/customers',
} as const

//---------------------------- ADMIN Routes ----------------------------
export const AdminRoutes = {
  BOOKINGS: '/admin/bookings',
  BEACH_PLAN: '/admin/beach-plan',
  ANALYTICS: '/admin/analytics',
  CUSTOMERS: '/admin/customers',
  CONFIGURATION_GENERAL: '/admin/configuration/general',
  CONFIGURATION_BEACH_PLAN: '/admin/configuration/beach-plan',
  CONFIGURATION_PAYMENT: '/admin/configuration/payment',
  CONFIGURATION_AVAILABILITY: '/admin/configuration/availability',
  CONFIGURATION_PRICING: '/admin/configuration/pricing',
  CONFIGURATION_EMAIL: '/admin/configuration/email',
} as const

//---------------------------- PUBLIC Routes ----------------------------
export enum PublicRoutes {
  LOGIN = '/login',
}

/*-------------------------------------------------------------------------------
*                               ROUTES MAPPING
-------------------------------------------------------------------------------*/

//---------------------------- PUBLIC Routes  Mapping ----------------------------
export const publicRoutesConfig: Route[] = [{ path: PublicRoutes.LOGIN, element: LoginPage }]

//---------------------------- CUSTOMER Routes  Mapping ----------------------------
export const customerRoutesConfig: Route[] = [
  { path: CustomerRoutes.HOME, element: HomePage },
  { path: CustomerRoutes.CHECKOUT, element: CheckoutPage },
  { path: CustomerRoutes.OUTCOME, element: OutcomePage },
  { path: CustomerRoutes.CONTACT, element: staticPageElement(StaticPages.CONTACT) },
  { path: CustomerRoutes.SERVICES, element: staticPageElement(StaticPages.SERVICES) },
  { path: CustomerRoutes.PRICELIST, element: staticPageElement(StaticPages.PRICELIST) },
  { path: CustomerRoutes.RESERVATION_POLICY, element: staticPageElement(StaticPages.RESERVATION_POLICY) },
  { path: CustomerRoutes.PRIVACY_POLICY, element: staticPageElement(StaticPages.PRIVACY_POLICY) },
]

//---------------------------- MANAGER Routes Mapping ----------------------------
export const managerRoutesConfig: Route[] = [
  { path: ManagerRoutes.BOOKINGS, element: BookingsPage },
  { path: ManagerRoutes.BEACH_PLAN, element: BeachPlanPage },
]

//---------------------------- WAITER Routes Mapping ----------------------------
export const waiterRoutesConfig: Route[] = [
  { path: WaiterRoutes.BOOKINGS, element: BookingsPage },
  { path: WaiterRoutes.BEACH_PLAN, element: BeachPlanPage },
]

//---------------------------- OWNER Routes Mapping ----------------------------
export const ownerRoutesConfig: Route[] = [
  { path: OwnerRoutes.BOOKINGS, element: BookingsPage },
  { path: OwnerRoutes.BEACH_PLAN, element: BeachPlanPage },
  { path: OwnerRoutes.ANALYTICS, element: AnalyticsPage },
  { path: OwnerRoutes.CUSTOMERS, element: CustomersPage, requiredPlan: PlanLevel.Standard },
]

//---------------------------- ADMIN Routes Mapping ----------------------------
export const adminRoutesConfig: Route[] = [
  { path: AdminRoutes.BOOKINGS, element: BookingsPage },
  { path: AdminRoutes.BEACH_PLAN, element: BeachPlanPage },
  { path: AdminRoutes.ANALYTICS, element: AnalyticsPage },
  { path: AdminRoutes.CUSTOMERS, element: CustomersPage },
  { path: AdminRoutes.CONFIGURATION_GENERAL, element: SettingsGeneralPage },
  { path: AdminRoutes.CONFIGURATION_BEACH_PLAN, element: SettingsBeachPlanPage },
  { path: AdminRoutes.CONFIGURATION_AVAILABILITY, element: SettingsAvailabilityPage },
  { path: AdminRoutes.CONFIGURATION_PRICING, element: SettingsPricingPage },
  { path: AdminRoutes.CONFIGURATION_PAYMENT, element: SettingsPaymentPage },
  { path: AdminRoutes.CONFIGURATION_EMAIL, element: EmailConfigurationPage },
]

/*-------------------------------------------------------------------------------
*                                MENUS
-------------------------------------------------------------------------------*/

//---------------------------- MANAGER Menu Items ----------------------------
export const useManagerMenuItems = (): MenuItem[] => {
  const { t } = useTranslation()
  return [
    { label: t('menu.bookings'), url: ManagerRoutes.BOOKINGS, icon: <BookingIcon /> },
    { label: t('menu.beachPlan'), url: ManagerRoutes.BEACH_PLAN, icon: <UmbrellaIcon /> },
  ]
}

//---------------------------- WAITER Menu Items ----------------------------
export const useWaiterMenuItems = (): MenuItem[] => {
  const { t } = useTranslation()
  return [
    { label: t('menu.bookings'), url: WaiterRoutes.BOOKINGS, icon: <BookingIcon /> },
    { label: t('menu.beachPlan'), url: WaiterRoutes.BEACH_PLAN, icon: <UmbrellaIcon /> },
  ]
}

//---------------------------- OWNER Menu Items ----------------------------
export const useOwnerMenuItems = (): MenuItem[] => {
  const { t } = useTranslation()
  return [
    { label: t('menu.bookings'), url: OwnerRoutes.BOOKINGS, icon: <BookingIcon /> },
    { label: t('menu.beachPlan'), url: OwnerRoutes.BEACH_PLAN, icon: <UmbrellaIcon /> },
    { label: t('menu.analytics'), url: OwnerRoutes.ANALYTICS, icon: <AnalyticsIcon /> },
    { label: t('menu.customers'), url: OwnerRoutes.CUSTOMERS, icon: <CustomersIcon />, requiredPlan: PlanLevel.Standard },
  ]
}

//---------------------------- ADMIN Menu Items ----------------------------
export const useAdminMenuItems = (): MenuItem[] => {
  const { t } = useTranslation()
  return [
    { label: t('menu.bookings'), url: AdminRoutes.BOOKINGS, icon: <BookingIcon /> },
    { label: t('menu.beachPlan'), url: AdminRoutes.BEACH_PLAN, icon: <UmbrellaIcon /> },
    { label: t('menu.analytics'), url: AdminRoutes.ANALYTICS, icon: <AnalyticsIcon /> },
    { label: t('menu.customers'), url: AdminRoutes.CUSTOMERS, icon: <CustomersIcon /> },
    { label: t('menu.generalSettings'), url: AdminRoutes.CONFIGURATION_GENERAL, icon: <SettingsIcon /> },
    { label: t('menu.beachPlanSettings'), url: AdminRoutes.CONFIGURATION_BEACH_PLAN, icon: <UmbrellaIcon /> },
    { label: t('menu.availabilitySettings'), url: AdminRoutes.CONFIGURATION_AVAILABILITY, icon: <EditCalendarIcon /> },
    { label: t('menu.pricingSettings'), url: AdminRoutes.CONFIGURATION_PRICING, icon: <PricingIcon /> },
    { label: t('menu.paymentSettings'), url: AdminRoutes.CONFIGURATION_PAYMENT, icon: <AddCardIcon /> },
    { label: t('menu.emailSettings'), url: AdminRoutes.CONFIGURATION_EMAIL, icon: <EmailIcon /> },
  ]
}
